import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { APIClient } from "../../../../helpers/api_helper";
import {
  formatNome,
  validateOperationForm,
  validateSubOperationForm,
} from "../utils/operationsUtils";

// AIDEV-NOTE: Custom hook for managing operation form state and business logic
const apiClient = new APIClient();

// AIDEV-NOTE: Helper function to get opposite direction for sub-operations
const getDirecaoSubOperacao = (direcaoPrincipal) => {
  return direcaoPrincipal === "compra" ? "venda" : "compra";
};

export const useOperationForm = () => {
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);

  // Main operation states
  const [cliente, setCliente] = useState(null);
  const [direcao, setDirecao] = useState("compra");
  const [moeda, setMoeda] = useState(null);

  // Calculated fields
  const [cotacao, setCotacao] = useState("");
  const [cotacaoFort, setCotacaoFort] = useState("");
  const [lucro, setLucro] = useState("");
  const [quantidade, setQuantidade] = useState("");

  // Toggle and calculations
  const [ajustarPor, setAjustarPor] = useState("taxa");
  const [taxaFortPercentage, setTaxaFortPercentage] = useState("1.45");
  const [taxaRedeBrl, setTaxaRedeBrl] = useState("0");
  const [totalBrl, setTotalBrl] = useState("");

  // Nota fiscal
  const [emitirNota, setEmitirNota] = useState(false);

  // Data lists
  const [listaClientes, setListaClientes] = useState([]);
  const [listaMoedas, setListaMoedas] = useState([]);
  const [moedas, setMoedas] = useState([]);

  // Sub-operations
  const [subOperacoes, setSubOperacoes] = useState([]);
  const [viewMode, setViewMode] = useState("list");

  // Build form data
  const [buildFormData, setBuildFormData] = useState({
    fornecedor_id: null,
    quantidade: "",
    total_brl: "",
  });

  // Fornecedores para o Select
  const [listaFornecedores, setListaFornecedores] = useState([]);

  // Modal de confirmação para limpeza de sub-operações
  const [modalConfirmacao, setModalConfirmacao] = useState(false);
  const [novaDirecao, setNovaDirecao] = useState(null);
  const [novaMoeda, setNovaMoeda] = useState(null);
  const [tipoMudanca, setTipoMudanca] = useState(null); // 'direcao' ou 'moeda'

  // Estados temporários para reversão
  const [tempDirecao, setTempDirecao] = useState(null);
  const [tempMoeda, setTempMoeda] = useState(null);

  // Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [availableSubOps, setAvailableSubOps] = useState([]);
  const [selectedSubOps, setSelectedSubOps] = useState([]);
  const [loadingSubOps, setLoadingSubOps] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // System config
  const [taxaFortPadrao, setTaxaFortPadrao] = useState(0);
  const [usdBrl, setUsdBrl] = useState(0);

  // Modal states
  const [modalFornecedor, setModalFornecedor] = useState(false);

  // Calculation control
  const [calculando, setCalculando] = useState(false);

  // AIDEV-TODO: Complex calculation logic with field dependencies
  const recalcular = useCallback(() => {
    if (calculando || !moeda?.data) return;

    setCalculando(true);

    try {
      let cotacaoBase = 0;
      if (direcao === "compra") {
        cotacaoBase = moeda.data.preco_buy || moeda.data.preco_atual_brl || 0;
      } else {
        cotacaoBase =
          moeda.data.preco_conversao || moeda.data.preco_atual_brl || 0;
      }
      setCotacao(cotacaoBase);

      const taxaDecimal = parseFloat(taxaFortPercentage) / 100;
      let cotacaoFortCalculada = 0;

      if (direcao === "compra") {
        cotacaoFortCalculada = cotacaoBase * (1 + taxaDecimal);
      } else {
        cotacaoFortCalculada = cotacaoBase * (1 - taxaDecimal);
      }
      setCotacaoFort(cotacaoFortCalculada);

      const taxaRedeNum = parseFloat(taxaRedeBrl) || 0;

      if (ajustarPor === "taxa") {
        const quantidadeNum = parseFloat(quantidade) || 0;
        const subtotal = quantidadeNum * cotacaoFortCalculada;

        // AIDEV-NOTE: Include network fee in total calculation
        let totalFinal;
        if (direcao === "compra") {
          totalFinal = subtotal + taxaRedeNum; // Client pays more
        } else {
          totalFinal = subtotal - taxaRedeNum; // Client receives less
        }

        setTotalBrl(totalFinal.toString());

        const lucroCalculado =
          quantidadeNum * (cotacaoFortCalculada - cotacaoBase);
        setLucro(lucroCalculado.toString());
      } else if (ajustarPor === "total") {
        const totalNum = parseFloat(totalBrl) || 0;
        const quantidadeNum = parseFloat(quantidade) || 0;

        if (quantidadeNum > 0) {
          // AIDEV-NOTE: Adjust total for network fee before calculating quotation
          let subtotalAjustado;
          if (direcao === "compra") {
            subtotalAjustado = totalNum - taxaRedeNum; // Subtract fee from total
          } else {
            subtotalAjustado = totalNum + taxaRedeNum; // Add fee to total
          }

          const cotacaoNecessaria = subtotalAjustado / quantidadeNum;
          const taxaNecessaria =
            ((cotacaoNecessaria - cotacaoBase) / cotacaoBase) * 100;
          setTaxaFortPercentage(Math.abs(taxaNecessaria).toString());
          setCotacaoFort(cotacaoNecessaria);

          const lucroCalculado =
            quantidadeNum * (cotacaoNecessaria - cotacaoBase);
          setLucro(lucroCalculado.toString());
        }
      }
    } finally {
      setCalculando(false);
    }
  }, [
    moeda,
    direcao,
    taxaFortPercentage,
    taxaRedeBrl,
    quantidade,
    totalBrl,
    ajustarPor,
    calculando,
  ]);

  // Auto-recalculate when relevant fields change
  useEffect(() => {
    if (moeda && (quantidade || totalBrl)) {
      recalcular();
    }
  }, [
    moeda,
    direcao,
    taxaFortPercentage,
    taxaRedeBrl,
    quantidade,
    totalBrl,
    ajustarPor,
    recalcular,
  ]);

  // AIDEV-NOTE: Quantity is ALWAYS calculated from sub-operations sum (never user-editable)
  useEffect(() => {
    const somaQuantidade = subOperacoes.reduce((acc, sub) => {
      return acc + (parseFloat(sub.quantidade) || 0);
    }, 0);
    setQuantidade(somaQuantidade.toString());
  }, [subOperacoes]);

  // AIDEV-NOTE: Effect to handle direction changes with state reversion
  const previousDirecao = useRef(direcao);
  useEffect(() => {
    if (previousDirecao.current && previousDirecao.current !== direcao) {
      if (subOperacoes.length > 0) {
        // Save the new direction temporarily
        setTempDirecao(direcao);
        // Revert to previous direction immediately
        setDirecao(previousDirecao.current);
        setTipoMudanca("direcao");
        setModalConfirmacao(true);
        return;
      }
    }
    previousDirecao.current = direcao;
  }, [direcao, subOperacoes.length]);

  // AIDEV-NOTE: Effect to handle currency changes with state reversion
  const previousMoeda = useRef(moeda?.data?.moeda);
  const previousMoedaFull = useRef(moeda);
  useEffect(() => {
    if (previousMoeda.current && previousMoeda.current !== moeda?.data?.moeda) {
      if (subOperacoes.length > 0) {
        // Save the new currency temporarily
        setTempMoeda(moeda);
        // Revert to previous currency immediately
        setMoeda(previousMoedaFull.current);
        setTipoMudanca("moeda");
        setModalConfirmacao(true);
        return;
      }
    }
    previousMoeda.current = moeda?.data?.moeda;
    previousMoedaFull.current = moeda;
  }, [moeda, subOperacoes.length]);

  // API calls
  const fetchClientes = useCallback(async () => {
    const response = await apiClient.get("/client/clientes");
    if (response.sucesso) {
      const clientesVerificados = response.dados.filter(
        (cliente) =>
          cliente.metamap_status === "verification_completed" &&
          cliente.metamap_status_verification === "verified"
      );

      return clientesVerificados.map((cliente) => ({
        value: cliente.user_id,
        label: formatNome(cliente.nome_completo || cliente.name),
        data: cliente,
      }));
    } else {
      toast.error(response.mensagem);
      return [];
    }
  }, []);

  const fetchMoedas = useCallback(async () => {
    const response = await apiClient.get("/moedas/precos");
    if (response.sucesso) {
      const moedasData = response.dados;
      setMoedas(moedasData);

      return moedasData.map((moeda) => ({
        value: moeda.id,
        rede: moeda.rede,
        label: `${moeda.moeda} (${moeda.rede})`,
        data: moeda,
      }));
    } else {
      toast.error(response.mensagem);
      return [];
    }
  }, []);

  const fetchConfigData = useCallback(async () => {
    const response = await apiClient.get("/config-system");
    if (response.sucesso) {
      response.dados.forEach((item) => {
        if (item.campo === "taxa_compra_venda") {
          setTaxaFortPadrao(parseFloat(item.value));
          setTaxaFortPercentage(parseFloat(item.value).toString());
        }
      });
    } else {
      toast.error(response.mensagem);
    }
  }, []);

  const fetchFornecedores = useCallback(async () => {
    const response = await apiClient.get("/fornecedores");
    if (response.sucesso) {
      return response.dados.map((fornecedor) => ({
        value: fornecedor.id,
        label: fornecedor.nome,
        data: fornecedor,
      }));
    } else {
      toast.error(response.mensagem);
      return [];
    }
  }, []);

  const fetchAvailableSubOps = useCallback(async () => {
    if (!moeda?.data?.moeda || viewMode !== "add") return;

    setLoadingSubOps(true);

    try {
      // AIDEV-NOTE: Buscar operações existentes (sub_op=false) com direção CONTRÁRIA
      const response = await apiClient.post("/operacao/busca", {
        moeda: moeda?.data?.moeda,
        direcao: getDirecaoSubOperacao(direcao),
        termo: searchTerm,
        pagina: currentPage,
        itens_pagina: 10,
        status: "completada",
        sub_op: false, // Apenas operações que podem ser referenciadas
      });

      if (response.sucesso) {
        // AIDEV-NOTE: Filter out operations that are already added as sub-operations
        const alreadyAddedIds = subOperacoes
          .filter(sub => sub.sub_op_id > 0)
          .map(sub => sub.sub_op_id);
        
        const filteredOps = (response.dados.data || []).filter(
          op => !alreadyAddedIds.includes(op.id)
        );
        
        setAvailableSubOps(filteredOps);
        setTotalPages(response.dados.last_page || 1);
      } else {
        toast.error(response.mensagem);
      }
    } catch (error) {
      toast.error("Erro ao buscar operações disponíveis");
    } finally {
      setLoadingSubOps(false);
    }
  }, [moeda, direcao, searchTerm, currentPage, viewMode, subOperacoes]);

  // Initial data loading
  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        setLoading(true);

        try {
          const [clientesData, moedasData, fornecedoresData] =
            await Promise.all([
              fetchClientes(),
              fetchMoedas(),
              fetchFornecedores(),
            ]);

          setListaClientes(clientesData);
          setListaMoedas(moedasData);
          setListaFornecedores(fornecedoresData);

          await fetchConfigData();

          const usdt = moedasData.find((m) => m.data.moeda === "USDT");
          if (usdt) {
            setUsdBrl(usdt.data.preco_atual_brl);
          }
        } catch (error) {
          toast.error("Erro ao carregar dados iniciais");
        } finally {
          setLoading(false);
          setShouldFetch(false);
        }
      };

      fetchData();
    }
  }, [
    shouldFetch,
    fetchClientes,
    fetchMoedas,
    fetchFornecedores,
    fetchConfigData,
  ]);

  // Sub-operations search effect
  useEffect(() => {
    if (viewMode === "add" && moeda?.data?.moeda) {
      fetchAvailableSubOps();
    }
  }, [fetchAvailableSubOps, viewMode, moeda]);

  // Form handlers
  const handleBuildFormChange = (field, value) => {
    setBuildFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBuildSubOperation = async () => {
    if (
      !buildFormData.fornecedor_id ||
      !buildFormData.quantidade ||
      !buildFormData.total_brl
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (!moeda?.data?.moeda) {
      toast.error("Selecione uma moeda antes de adicionar sub-operações");
      return;
    }

    const fornecedorSelecionado = listaFornecedores.find(
      (f) => f.value === buildFormData.fornecedor_id
    );

    const novaSubOperacao = {
      fornecedor_id: buildFormData.fornecedor_id,
      sub_op_id: 0, // Nova sub-operação com fornecedor
      fornecedor_nome: fornecedorSelecionado?.label || "",
      direcao: getDirecaoSubOperacao(direcao), // Direção contrária à principal
      moeda: moeda?.data?.moeda,
      quantidade: buildFormData.quantidade,
      total_brl: buildFormData.total_brl,
    };

    setSubOperacoes((prev) => [...prev, novaSubOperacao]);

    setBuildFormData({
      fornecedor_id: null,
      quantidade: "",
      total_brl: "",
    });

    setViewMode("list");
    toast.success("Sub-operação adicionada com sucesso");
  };

  const handleSelectSubOperation = (subOp) => {
    const isSelected = selectedSubOps.some((s) => s.id === subOp.id);

    if (isSelected) {
      setSelectedSubOps((prev) => prev.filter((s) => s.id !== subOp.id));
    } else {
      setSelectedSubOps((prev) => [...prev, subOp]);
    }
  };

  const handleAddSelectedSubOperations = () => {
    if (selectedSubOps.length === 0) {
      toast.error("Selecione pelo menos uma operação");
      return;
    }

    if (!moeda?.data?.moeda) {
      toast.error("Selecione uma moeda antes de adicionar operações");
      return;
    }

    // AIDEV-NOTE: Converter operações selecionadas em sub-operações com sub_op_id > 0
    const novasSubOperacoes = selectedSubOps.map((operacao) => ({
      fornecedor_id: null, // Não é necessário quando sub_op_id > 0
      sub_op_id: operacao.id, // Referência para operação existente
      fornecedor_nome: operacao.user?.name || "N/A", // Nome do usuário da operação
      direcao: getDirecaoSubOperacao(direcao), // Direção contrária à principal
      moeda: moeda?.data?.moeda, // Usar moeda do form principal
      quantidade: operacao.quantidade,
      total_brl: operacao.total_brl,
    }));

    setSubOperacoes((prev) => [...prev, ...novasSubOperacoes]);
    setSelectedSubOps([]);
    setViewMode("list");

    toast.success(
      `${selectedSubOps.length} operação(ões) adicionada(s) como sub-operação`
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateOperationForm(
      cliente,
      moeda,
      quantidade,
      totalBrl,
      subOperacoes
    );

    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    setSubmitting(true);

    try {
      const operacaoData = {
        user_id: cliente.value,
        direcao: direcao,
        moeda: moeda?.data?.moeda,
        rede: moeda?.data?.rede,
        quantidade: parseFloat(quantidade),
        cotacao: parseFloat(cotacao),
        cotacao_fort: parseFloat(cotacaoFort),
        taxa_fort_percentage: parseFloat(taxaFortPercentage),
        taxa_rede_brl: parseFloat(taxaRedeBrl),
        total_brl: parseFloat(totalBrl),
        lucro: parseFloat(lucro),
        emitir_nota: emitirNota,
        sub_operacoes: subOperacoes.map((sub) => ({
          fornecedor_id: sub.fornecedor_id || null,
          sub_op_id: sub.sub_op_id || 0,
          direcao: sub.direcao,
          moeda: sub.moeda,
          quantidade: parseFloat(sub.quantidade),
          total_brl: parseFloat(sub.total_brl),
        })),
      };

      const response = await apiClient.post("/operacao", operacaoData);

      if (response.sucesso) {
        toast.success("Operação criada com sucesso! Redirecionando...");

        setTimeout(() => {
          navigate("/operacoes");
        }, 2000);
      } else {
        toast.error(response.mensagem || "Erro ao criar operação");
      }
    } catch (error) {
      toast.error("Erro ao criar operação");
    } finally {
      setSubmitting(false);
    }
  };

  const onFornecedorAdded = async (novoFornecedor) => {
    // Atualizar lista de fornecedores
    const novaOpcao = {
      value: novoFornecedor.id,
      label: novoFornecedor.nome,
      data: novoFornecedor,
    };

    setListaFornecedores((prev) => [...prev, novaOpcao]);

    // Selecionar automaticamente o novo fornecedor
    setBuildFormData((prev) => ({
      ...prev,
      fornecedor_id: novoFornecedor.id,
    }));

    toast.success(`Fornecedor ${novoFornecedor.nome} adicionado com sucesso`);
  };

  // AIDEV-NOTE: Modal confirmation handlers with state reversion logic
  const handleConfirmLimparSubOperacoes = () => {
    // Apply the temporary changes
    if (tipoMudanca === "direcao" && tempDirecao) {
      setDirecao(tempDirecao);
      previousDirecao.current = tempDirecao; // Update ref to prevent loop
    }
    if (tipoMudanca === "moeda" && tempMoeda) {
      setMoeda(tempMoeda);
      previousMoeda.current = tempMoeda?.data?.moeda; // Update ref to prevent loop
      previousMoedaFull.current = tempMoeda;
    }

    // Clear sub-operations and states
    setSubOperacoes([]);
    setSelectedSubOps([]);
    setViewMode("list");
    setNovaDirecao(null);
    setNovaMoeda(null);
    setTempDirecao(null);
    setTempMoeda(null);
    setTipoMudanca(null);

    const mensagem =
      tipoMudanca === "direcao"
        ? "Sub-operações removidas devido à mudança de direção"
        : "Sub-operações removidas devido à mudança de moeda";

    toast.info(mensagem);
  };

  const toggleModalConfirmacao = () => {
    if (modalConfirmacao) {
      // Cancel: clear temporary states (original state already restored)
      setNovaDirecao(null);
      setNovaMoeda(null);
      setTempDirecao(null);
      setTempMoeda(null);
      setTipoMudanca(null);
    }
    setModalConfirmacao(!modalConfirmacao);
  };

  return {
    // States
    loading,
    submitting,
    cliente,
    direcao,
    moeda,
    cotacao,
    cotacaoFort,
    lucro,
    quantidade,
    ajustarPor,
    taxaFortPercentage,
    taxaRedeBrl,
    totalBrl,
    emitirNota,
    listaClientes,
    listaMoedas,
    listaFornecedores,
    subOperacoes,
    viewMode,
    buildFormData,
    searchTerm,
    availableSubOps,
    selectedSubOps,
    loadingSubOps,
    currentPage,
    totalPages,
    usdBrl,
    modalFornecedor,

    // Setters
    setCliente,
    setDirecao,
    setMoeda,
    setQuantidade,
    setAjustarPor,
    setTaxaFortPercentage,
    setTaxaRedeBrl,
    setTotalBrl,
    setEmitirNota,
    setSubOperacoes,
    setViewMode,
    setSearchTerm,
    setCurrentPage,
    setModalFornecedor,

    // Handlers
    handleBuildFormChange,
    handleBuildSubOperation,
    handleSelectSubOperation,
    handleAddSelectedSubOperations,
    handleSubmit,
    onFornecedorAdded,
    fetchAvailableSubOps,

    // Modal confirmation
    modalConfirmacao,
    toggleModalConfirmacao,
    handleConfirmLimparSubOperacoes,
    novaDirecao,
    novaMoeda,
    tipoMudanca,
  };
};
